/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Card, Chip, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, TextField, InputAdornment, Button, Typography,
  Table, TableBody, TableCell, TableRow, Alert, Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NoteIcon from '@mui/icons-material/Note';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ReplayIcon from '@mui/icons-material/Replay';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TimelineIcon from '@mui/icons-material/Timeline';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import MDBox from 'components/MDBox';
import TimelineDialog from '../components/TimelineDialog';
import DebitNoteDialog from './DebitNoteDialog';
import { api } from '../../../api';
import { usePharmacy } from '../../../contexts/PharmacyContext';
import { useListUrlState } from '../../../hooks/useListUrlState';
import { useListKeyboard, ACTIVE_ROW_SX } from 'hooks/useListKeyboard';
import { patientBarcodeStrip, barcodeScriptBlock, resolveCoupleSpouseCodes } from 'utils/printBarcodes';
import irmHeader from 'assets/images/irm-header.png';

const PURPLE = '#4a148c';
const PURPLE_MID = '#7b1fa2';
const PURPLE_LIGHT = '#f3e5f5';

const fmt = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const fmtDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const ADJ_LABEL = { INVOICE: 'Invoice Adj.', CASH: 'Cash Refund', ONLINE: 'Online/UPI' };
const ADJ_COLOR = {
  INVOICE: { bg: '#e3f2fd', color: '#1565c0', border: '#90caf9' },
  CASH:    { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
  ONLINE:  { bg: '#fff3e0', color: '#e65100', border: '#ffcc80' },
};

const STATUS = {
  DRAFT:     { bg: '#fff8e1', color: '#f57f17', border: '#ffe082', label: 'Draft',     dot: '#ffc107' },
  APPLIED:   { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7', label: 'Applied',   dot: '#43a047' },
  CANCELLED: { bg: '#f5f5f5', color: '#757575', border: '#e0e0e0', label: 'Cancelled', dot: '#bdbdbd' },
};

// ── Print ──────────────────────────────────────────────────────────────────────
function amountInWords(amount) {
  if (!amount && amount !== 0) return '';
  const n = Math.abs(Math.round(amount * 100));
  const paise = n % 100; const rupees = Math.floor(n / 100);
  const ones = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const tens = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  const toW = (num) => {
    if (num === 0) return '';
    if (num < 20) return ones[num];
    if (num < 100) return tens[Math.floor(num/10)] + (num%10 ? ' '+ones[num%10] : '');
    return ones[Math.floor(num/100)] + ' Hundred' + (num%100 ? ' '+toW(num%100) : '');
  };
  const inIndian = (num) => {
    if (num === 0) return 'Zero'; let out = '';
    if (num >= 10000000) { out += toW(Math.floor(num/10000000))+' Crore '; num %= 10000000; }
    if (num >= 100000)   { out += toW(Math.floor(num/100000))  +' Lakh ';  num %= 100000; }
    if (num >= 1000)     { out += toW(Math.floor(num/1000))    +' Thousand '; num %= 1000; }
    if (num > 0)         { out += toW(num); } return out.trim();
  };
  let out = inIndian(rupees);
  if (paise > 0) out += ` and ${toW(paise)} Paise`;
  return 'INR ' + out.trim();
}

function printDebitNote(dn, ph) {
  if (!dn) return;
  const PRIMARY = '#4a148c';
  const LIGHT_BG = '#f3e5f5';
  const BORDER = '#ce93d8';
  const cn = dn.credit_note || {};
  const bill = cn.original_bill || dn.invoice || {};
  const adjLabel = ADJ_LABEL[dn.adjustment_type] || dn.adjustment_type;
  const fmtD = (d) => { if (!d) return '—'; return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); };
  const C = `border:1px solid ${BORDER};padding:3px 6px;font-size:7.5pt;`;
  const CH = `${C}background:${LIGHT_BG};color:${PRIMARY};font-weight:700;`;
  const statusSt = { DRAFT: 'background:#fff8e1;color:#f57f17;border:1px solid #ffe082', APPLIED: 'background:#e8f5e9;color:#2e7d32;border:1px solid #a5d6a7', CANCELLED: 'background:#f5f5f5;color:#757575;border:1px solid #e0e0e0' };

  // Barcodes get their own full-height column (rowspan) beside the
  // pharmacy/reference info, instead of being crammed under the patient
  // info — keeps both left columns a natural, even height.
  const { coupleCode, spouseCode } = resolveCoupleSpouseCodes(bill.contactInfo);
  const { html: barcodeHtml, initScript: barcodeInit } = patientBarcodeStrip({
    idPrefix: 'bc-dn',
    patientCode: bill.contactInfo?.external_code,
    coupleCode,
    spouseCode,
    documentCode: dn.debit_note_no,
    documentLabel: 'DN No',
  });
  const barcodeCell = barcodeHtml
    ? `<td rowspan="2" style="width:22%;padding:5px 8px;vertical-align:top">${barcodeHtml}</td>`
    : '';
  const leftColWidth = barcodeHtml ? '39%' : '50%';

  const body = `
    <div style="margin-bottom:6px">
      <div style="line-height:0">
        <img src="${irmHeader}" alt="Company Header" style="width:100%;object-fit:contain;display:block">
      </div>
      <div style="text-align:center;margin-top:5px">
        <div style="font-size:14pt;font-weight:700;letter-spacing:1.5px;font-family:'Montserrat',Arial,sans-serif;color:${PRIMARY}">DEBIT NOTE</div>
      </div>
    </div>
    <table style="width:100%;border-collapse:collapse;border:1.5px solid ${BORDER}">
      <tr>
        <td style="width:${leftColWidth};border-right:1px solid ${BORDER};padding:5px 8px;vertical-align:top">
          <div style="font-size:11pt;font-weight:900;color:${PRIMARY};line-height:1.1">${ph?.name || 'PHARMACY'}</div>
          ${ph?.sub_title ? `<div style="font-size:7.5pt;color:#444;margin-top:1px">${ph.sub_title}</div>` : ''}
          <div style="font-size:7pt;color:#333;margin-top:2px;line-height:1.5">${ph?.address || ''}${ph?.city ? ', '+ph.city : ''}${ph?.pincode ? ' - '+ph.pincode : ''}</div>
          ${ph?.dl_no ? `<div style="font-size:6.5pt;color:#555">DL No : ${ph.dl_no}</div>` : ''}
          ${ph?.gstin ? `<div style="font-size:6.5pt;color:#555">GSTIN : ${ph.gstin}</div>` : ''}
          ${ph?.gstin_uin && ph.gstin_uin !== ph.gstin ? `<div style="font-size:6.5pt;color:#555">GSTIN/UIN : ${ph.gstin_uin}</div>` : ''}
          ${ph?.state ? `<div style="font-size:6.5pt;color:#555">State Name : ${ph.state}${ph.state_code ? ', Code : '+ph.state_code : ''}</div>` : ''}
          ${ph?.email ? `<div style="font-size:6.5pt;color:#555">E-Mail : ${ph.email}</div>` : ''}
          ${ph?.mobile ? `<div style="font-size:6.5pt;color:#555">Mobile : ${ph.mobile}</div>` : ''}
        </td>
        <td style="width:${leftColWidth};padding:5px 8px;vertical-align:top;${barcodeHtml ? `border-right:1px solid ${BORDER}` : ''}">
          <table style="width:100%;border-collapse:collapse;font-size:7.5pt">
            <tr><td style="color:#777;padding-bottom:2px;padding-right:8px;white-space:nowrap">No.</td><td style="font-weight:700;padding-bottom:2px">${dn.debit_note_no}</td></tr>
            <tr><td style="color:#777;padding-bottom:2px;padding-right:8px;white-space:nowrap">Date</td><td style="font-weight:700;padding-bottom:2px">${fmtD(dn.debit_note_date)}</td></tr>
            <tr><td style="color:#777;padding-right:8px;white-space:nowrap">Status</td><td><span style="padding:2px 7px;border-radius:3px;font-size:7pt;font-weight:700;${statusSt[dn.status] || 'background:#eee;color:#555'}">${STATUS[dn.status]?.label || dn.status}</span></td></tr>
            <tr><td style="color:#777;padding-top:3px;padding-right:8px;white-space:nowrap">Adj. Method</td><td style="font-weight:600;padding-top:3px">${adjLabel}</td></tr>
          </table>
        </td>
        ${barcodeCell}
      </tr>
      <tr style="border-top:1px solid ${BORDER}">
        <td style="width:${leftColWidth};border-right:1px solid ${BORDER};padding:5px 8px;font-size:7.5pt;vertical-align:top">
          <div style="font-size:7pt;color:${PRIMARY};font-weight:700;text-transform:uppercase;margin-bottom:2px">Patient</div>
          <div style="font-weight:700;font-size:9pt">${bill.patient_name || cn.patient_name || '—'}</div>
          ${(bill.patient_mobile || cn.patient_mobile) ? `<div style="font-size:7.5pt;color:#555">Mob: ${bill.patient_mobile || cn.patient_mobile}</div>` : ''}
          ${(bill.patient_age || cn.patient_age) ? `<div style="font-size:7.5pt;color:#555">Age: ${bill.patient_age || cn.patient_age}</div>` : ''}
        </td>
        <td style="width:${leftColWidth};padding:5px 8px;font-size:7.5pt;vertical-align:top">
          <div style="font-size:7pt;color:${PRIMARY};font-weight:700;text-transform:uppercase;margin-bottom:2px">Reference Credit Note</div>
          <div style="font-weight:700">${cn.credit_note_no || '—'}</div>
          ${dn.invoice ? `<div style="font-size:7.5pt;color:#777;margin-top:3px">Adjusted against Bill: <b>${dn.invoice.bill_no || '—'}</b></div>` : ''}
        </td>
      </tr>
    </table>
    ${barcodeScriptBlock(barcodeInit)}
    <table style="width:100%;border-collapse:collapse;border:1px solid ${BORDER};border-top:none">
      <tr><th style="${CH}text-align:left">Description</th><th style="${CH}width:35%;text-align:right">Value</th></tr>
      <tr><td style="${C}">Debit Note Amount</td><td style="${C}text-align:right;font-family:monospace;font-weight:700">${Number(dn.amount).toFixed(2)}</td></tr>
      ${dn.payment_reference ? `<tr><td style="${C}color:#888">Payment Ref / UTR</td><td style="${C}text-align:right;font-family:monospace">${dn.payment_reference}</td></tr>` : ''}
      ${dn.bank_name ? `<tr><td style="${C}color:#888">Bank</td><td style="${C}text-align:right">${dn.bank_name}</td></tr>` : ''}
      <tr style="background:${LIGHT_BG}"><td style="${C}font-weight:700">Amount</td><td style="${C}text-align:right;font-family:monospace;font-weight:800;font-size:10.5pt;color:${PRIMARY}">${Number(dn.amount).toFixed(2)}</td></tr>
    </table>
    <table style="width:100%;border-collapse:collapse;border:1px solid ${BORDER};border-top:none;font-size:7.5pt">
      <tr>
        <td style="padding:4px 7px;border-right:1px solid ${BORDER};font-weight:700;color:#555;white-space:nowrap;font-size:7pt">Amount Chargeable (in words)</td>
        <td style="padding:4px 10px;font-style:italic">${amountInWords(Number(dn.amount))} Only</td>
        <td style="padding:4px 7px;border-left:1px solid ${BORDER};font-size:7pt;color:#888;text-align:right;white-space:nowrap">E. &amp; O.E.</td>
      </tr>
    </table>
    ${dn.reason ? `<div style="margin-top:4px;padding:4px 8px;font-size:7.5pt;color:#666;font-style:italic;border:1px solid ${BORDER};border-radius:2px"><b>Reason:</b> ${dn.reason}</div>` : ''}
    <table style="width:100%;border-collapse:collapse;margin-top:5px;border:1px solid ${BORDER};font-size:7.5pt">
      <tr>
        <td style="width:45%;vertical-align:top;border-right:1px solid ${BORDER};padding:5px 7px">
          ${ph?.pan_no ? `<div style="margin-bottom:3px"><b>Company&apos;s PAN:</b> <span style="font-family:monospace;letter-spacing:0.8px">${ph.pan_no}</span></div>` : ''}
          ${ph?.declaration ? `<div style="font-size:6.5pt;font-weight:700;color:${PRIMARY};text-transform:uppercase;margin-top:${ph.pan_no?'4px':'0'};margin-bottom:2px">Declaration</div><div style="font-size:6.5pt;color:#555;line-height:1.5">${ph.declaration}</div>` : ''}
        </td>
        <td style="width:30%;vertical-align:top;border-right:1px solid ${BORDER};padding:5px 7px">
          ${ph?.bank_name ? `
            <div style="font-size:6.5pt;font-weight:700;color:${PRIMARY};text-transform:uppercase;margin-bottom:3px">Company&apos;s Bank Details</div>
            <table style="font-size:7pt;border-collapse:collapse;width:100%">
              <tr><td style="color:#777;padding-bottom:2px;white-space:nowrap;padding-right:4px">Bank Name</td><td style="font-weight:600;padding-bottom:2px">: ${ph.bank_name}</td></tr>
              ${ph.account_no ? `<tr><td style="color:#777;padding-bottom:2px;white-space:nowrap;padding-right:4px">A/c No.</td><td style="font-weight:600;padding-bottom:2px">: ${ph.account_no}</td></tr>` : ''}
              ${ph.bank_branch ? `<tr><td style="color:#777;padding-bottom:2px;white-space:nowrap;padding-right:4px">Branch</td><td style="font-weight:600;padding-bottom:2px">: ${ph.bank_branch}</td></tr>` : ''}
              ${ph.ifsc_code ? `<tr><td style="color:#777;padding-bottom:2px;white-space:nowrap;padding-right:4px">IFS Code</td><td style="font-weight:600;padding-bottom:2px">: ${ph.ifsc_code}</td></tr>` : ''}
            </table>
          ` : ''}
        </td>
        <td style="width:25%;vertical-align:bottom;text-align:center;padding:5px 7px;padding-top:16px">
          <div style="font-size:7.5pt;font-weight:700;color:#333;margin-bottom:16px">for ${ph?.name || ''}</div>
          <div style="border-top:1.5px solid #333;padding-top:4px;font-size:8pt;font-weight:700">Authorised Signatory</div>
        </td>
      </tr>
      <tr style="border-top:1px solid ${BORDER}">
        <td colspan="2" style="padding:4px 7px;border-right:1px solid ${BORDER};vertical-align:middle">
          <div style="height:30px;border:1px dashed #ccc;border-radius:3px;display:flex;align-items:center;justify-content:center;color:#ccc;font-style:italic;font-size:7pt">Authorised Seal</div>
        </td>
        <td style="padding:4px 7px;text-align:center;vertical-align:middle;font-size:6.5pt;color:#aaa;font-style:italic">
          This is a Computer Generated Document
        </td>
      </tr>
    </table>
  `;

  const win = window.open('', '_blank', 'width=960,height=720');
  const toolbar = `<div id="ptb" style="position:fixed;top:0;left:0;right:0;background:#4a148c;color:#fff;padding:9px 16px;display:flex;justify-content:space-between;align-items:center;z-index:9999;font-family:Arial,sans-serif;box-shadow:0 2px 8px rgba(0,0,0,0.25)"><span style="font-weight:700;font-size:10.5pt">Debit Note &mdash; ${dn.debit_note_no}</span><div style="display:flex;gap:8px"><button onclick="window.print()" style="background:#fff;color:#4a148c;border:none;padding:7px 16px;border-radius:4px;font-weight:700;cursor:pointer;font-size:9pt">&#128438; Print</button><button onclick="window.print()" style="background:rgba(255,255,255,0.18);color:#fff;border:1px solid rgba(255,255,255,0.5);padding:7px 16px;border-radius:4px;font-weight:700;cursor:pointer;font-size:9pt">&#8595; Download PDF</button><button onclick="window.close()" style="background:transparent;color:rgba(255,255,255,0.8);border:1px solid rgba(255,255,255,0.4);padding:7px 10px;border-radius:4px;font-weight:700;cursor:pointer;font-size:9pt">&#10005;</button></div></div>`;
  win.document.write(`<!DOCTYPE html><html><head><title>Debit Note ${dn.debit_note_no}</title>
    <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700;900&display=swap">
    <style>@page{size:A5 portrait;margin:10mm}*{box-sizing:border-box}body{font-family:'Montserrat',Arial,sans-serif;margin:0;padding:50px 0 0 0;-webkit-print-color-adjust:exact;print-color-adjust:exact}td,th{overflow-wrap:break-word}@media print{#ptb{display:none!important}body{padding-top:0!important}}</style>
    </head><body>${toolbar}<div class="pf-page" style="width:484px;margin:0 auto">${body}</div>
    <script>window.onload=function(){try{var els=document.querySelectorAll('.pf-page');for(var i=0;i<els.length;i++){var h=els[i].scrollHeight;if(h>718)els[i].style.zoom=Math.max(0.55,718/h);}}catch(e){}};</script>
    </body></html>`);
  win.document.close();
}

// ── Stat Card ──────────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, accent, sub }) {
  return (
    <Box sx={{
      flex: '1 1 160px', minWidth: 140, borderRadius: 3, background: '#fff',
      border: `1.5px solid ${accent}33`, boxShadow: `0 2px 12px ${accent}18`,
      px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1.8,
      transition: 'box-shadow 0.2s', '&:hover': { boxShadow: `0 6px 24px ${accent}28` },
    }}>
      <Box sx={{ width: 44, height: 44, borderRadius: 2.5, background: `linear-gradient(135deg, ${accent}22, ${accent}44)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {React.cloneElement(icon, { sx: { color: accent, fontSize: 22 } })}
      </Box>
      <Box>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: 0.5, lineHeight: 1.2 }}>{label}</Typography>
        <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#1a202c', lineHeight: 1.4, fontFamily: 'monospace' }}>{value}</Typography>
        {sub && <Typography sx={{ fontSize: '0.65rem', color: '#bdbdbd', mt: 0.2 }}>{sub}</Typography>}
      </Box>
    </Box>
  );
}

// ── Status Badge ───────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.CANCELLED;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, px: 1.2, py: 0.35, borderRadius: '20px', background: s.bg, border: `1px solid ${s.border}` }}>
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: s.dot }} />
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.label}</Typography>
    </Box>
  );
}

// ── Adj Type Badge ─────────────────────────────────────────────────────────────
function AdjBadge({ type }) {
  const c = ADJ_COLOR[type] || ADJ_COLOR.CASH;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', px: 1, py: 0.3, borderRadius: 1.5, background: c.bg, border: `1px solid ${c.border}` }}>
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: c.color }}>{ADJ_LABEL[type] || type}</Typography>
    </Box>
  );
}

// ── Cancel Dialog ──────────────────────────────────────────────────────────────
function CancelDialog({ open, dn, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { if (open) { setReason(''); setErr(''); } }, [open]);

  const handleConfirm = async () => {
    setLoading(true); setErr('');
    try { await onConfirm({ cancel_reason: reason.trim() || undefined }); }
    catch (e) { setErr(e?.message || 'Cancellation failed'); }
    finally { setLoading(false); }
  };

  if (!dn) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ background: 'linear-gradient(135deg, #b71c1c, #d32f2f)', px: 2.5, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1rem' }}>Cancel Debit Note</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.72rem', mt: 0.25 }}>This action cannot be undone</Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: 'rgba(255,255,255,0.8)' }}><CloseIcon fontSize="small" /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5, px: 2.5 }}>
        <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, background: '#fff8e1', border: '1px solid #ffe082' }}>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#4a148c', fontFamily: 'monospace' }}>{dn.debit_note_no}</Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#555', mt: 0.3 }}>{fmt(dn.amount)} · {ADJ_LABEL[dn.adjustment_type] || dn.adjustment_type}</Typography>
        </Box>
        <TextField fullWidth size="small" label="Reason (optional)" value={reason} onChange={e => setReason(e.target.value)}
          multiline rows={2} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.8rem' } }} />
        {err && <Alert severity="error" sx={{ mt: 1.5, fontSize: '0.78rem', py: 0.5, borderRadius: 2 }}>{err}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} sx={{ fontSize: '0.78rem', color: '#9e9e9e', fontWeight: 600 }}>Keep It</Button>
        <Button onClick={handleConfirm} variant="contained" disabled={loading}
          sx={{ background: 'linear-gradient(135deg, #b71c1c, #d32f2f)', color: '#fff !important', fontSize: '0.78rem', fontWeight: 700, px: 2.5, borderRadius: 2 }}>
          {loading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : 'Cancel Debit Note'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Apply Confirm Dialog ───────────────────────────────────────────────────────
function ApplyDialog({ open, dn, onClose, onConfirm }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { if (open) setErr(''); }, [open]);

  const handleConfirm = async () => {
    setLoading(true); setErr('');
    try { await onConfirm(); }
    catch (e) { setErr(e?.message || 'Failed to apply debit note'); }
    finally { setLoading(false); }
  };

  if (!dn) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ background: `linear-gradient(135deg, ${PURPLE}, ${PURPLE_MID})`, px: 2.5, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1rem' }}>Apply Debit Note</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.72rem', mt: 0.25 }}>This will mark the credit note as USED</Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: 'rgba(255,255,255,0.8)' }}><CloseIcon fontSize="small" /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5, px: 2.5 }}>
        <Box sx={{ mb: 1, p: 1.5, borderRadius: 2, background: PURPLE_LIGHT, border: `1px solid #ce93d8` }}>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: PURPLE, fontFamily: 'monospace' }}>{dn.debit_note_no}</Typography>
          <Typography sx={{ fontSize: '0.78rem', color: '#555', mt: 0.25 }}>Amount: {fmt(dn.amount)} · {ADJ_LABEL[dn.adjustment_type] || dn.adjustment_type}</Typography>
          {dn.credit_note && <Typography sx={{ fontSize: '0.72rem', color: '#888', mt: 0.2 }}>CN: {dn.credit_note.credit_note_no}</Typography>}
        </Box>
        <Typography sx={{ fontSize: '0.78rem', color: '#555' }}>
          Applying this debit note will settle the linked credit note. Journal entries will be posted automatically.
        </Typography>
        {err && <Alert severity="error" sx={{ mt: 1.5, fontSize: '0.78rem', py: 0.5, borderRadius: 2 }}>{err}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} sx={{ fontSize: '0.78rem', color: '#9e9e9e', fontWeight: 600 }}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained" disabled={loading}
          sx={{ background: `linear-gradient(135deg, ${PURPLE}, ${PURPLE_MID})`, color: '#fff !important', fontSize: '0.78rem', fontWeight: 700, px: 2.5, borderRadius: 2 }}>
          {loading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : 'Apply Now'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function DebitNotesPage() {
  const navigate = useNavigate();
  const { selectedPharmacy } = usePharmacy();
  const { readParams, writeParams } = useListUrlState();
  const urlParams = readParams();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState((urlParams && parseInt(urlParams.get('page'), 10)) || 1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(urlParams?.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(urlParams?.get('status') || '');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialogs
  const [cancelTarget, setCancelTarget] = useState(null);
  const [applyTarget, setApplyTarget] = useState(null);
  const [reissueTarget, setReissueTarget] = useState(null);
  const [reissueCn, setReissueCn] = useState(null);
  const [reissueLoading, setReissueLoading] = useState(false);

  // Timeline
  const [showTimeline, setShowTimeline] = useState(false);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineMeta, setTimelineMeta] = useState({ title: '', subtitle: '' });

  const searchTimer = useRef(null);
  const PER_PAGE = 20;

  const kb = useListKeyboard({
    rowCount: rows.length,
    enabled: !cancelTarget && !applyTarget && !reissueTarget && !showTimeline,
    onOpen: (i) => {
      const b = rows[i]?.credit_note?.original_bill;
      if (b?.id) navigate(`/pharmacy/billing/${b.id}`);
    },
  });

  const load = useCallback(async (q, status, pg) => {
    setLoading(true); setError('');
    try {
      writeParams({ search: q, status, page: pg });
      const params = { page: pg, per_page: PER_PAGE };
      if (q) params.search = q;
      if (status) params.status = status;
      const res = await api.pharmacy.listDebitNotes(params);
      setRows(res?.rows || []);
      setTotal(res?.count || 0);
    } catch (e) {
      setError(e?.message || 'Failed to load debit notes');
    } finally { setLoading(false); }
  }, [writeParams]);

  useEffect(() => { load(search, statusFilter, page); }, [page]); // eslint-disable-line

  const handleSearchChange = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setPage(1); load(val, statusFilter, 1); }, 400);
  };

  const handleStatusFilter = (s) => {
    const next = s === statusFilter ? '' : s;
    setStatusFilter(next); setPage(1); load(search, next, 1);
  };

  const handleCancel = async (dto) => {
    await api.pharmacy.cancelDebitNote(cancelTarget.id, dto);
    setCancelTarget(null);
    setSuccess('Debit note cancelled.');
    load(search, statusFilter, page);
  };

  const handleApply = async () => {
    await api.pharmacy.applyDebitNote(applyTarget.id);
    setApplyTarget(null);
    setSuccess('Debit note applied successfully. Credit note marked as USED.');
    load(search, statusFilter, page);
  };

  const handleReissue = async (dn) => {
    setReissueLoading(true);
    try {
      const cn = await api.pharmacy.getCreditNote(dn.irm_pharm_bill_credit_notes_id);
      const cnData = cn?.data || cn;
      if (!cnData || cnData.status !== 'OPEN') {
        setError('The associated credit note is no longer open and cannot be re-issued.');
        return;
      }
      setReissueCn(cnData);
      setReissueTarget(dn);
    } catch {
      setError('Failed to fetch credit note details for re-issue.');
    } finally { setReissueLoading(false); }
  };

  const openTimeline = async (dn) => {
    const originalBillId = dn.credit_note?.original_bill?.id;
    if (!originalBillId) { setError('No original bill linked to this debit note.'); return; }
    setTimelineMeta({
      title: dn.debit_note_no,
      subtitle: `${dn.credit_note?.original_bill?.bill_no || ''} · ${dn.credit_note?.original_bill?.patient_name || ''}`,
    });
    setShowTimeline(true);
    setTimelineLoading(true);
    try {
      const res = await api.pharmacy.getBillingTimeline(originalBillId);
      setTimelineEvents(Array.isArray(res) ? res : (res?.data || []));
    } catch { setTimelineEvents([]); }
    finally { setTimelineLoading(false); }
  };

  const totalPages = Math.ceil(total / PER_PAGE);
  const draftCount = rows.filter(r => r.status === 'DRAFT').length;
  const appliedCount = rows.filter(r => r.status === 'APPLIED').length;
  const appliedTotal = rows.filter(r => r.status === 'APPLIED').reduce((s, r) => s + Number(r.amount || 0), 0);

  const COL_HEADERS = ['DN No', 'Date', 'Credit Note', 'Patient', 'Adj. Type', 'Amount', 'Status', 'Actions'];

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox py={3} px={0}>
        {/* ── Hero Header ── */}
        <Box sx={{
          borderRadius: 4,
          background: `linear-gradient(135deg, ${PURPLE} 0%, ${PURPLE_MID} 60%, #9c27b0 100%)`,
          px: { xs: 2.5, md: 4 }, py: 3, mb: 3,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2,
          boxShadow: `0 8px 32px ${PURPLE}44`,
          position: 'relative', overflow: 'hidden',
        }}>
          <Box sx={{ position: 'absolute', top: -30, right: -30, width: 130, height: 130, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }} />
          <Box sx={{ position: 'absolute', bottom: -20, right: 80, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, zIndex: 1 }}>
            <IconButton onClick={() => navigate('/pharmacy/billing')}
              sx={{ background: 'rgba(255,255,255,0.15)', color: '#fff', '&:hover': { background: 'rgba(255,255,255,0.28)' }, borderRadius: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ width: 46, height: 46, borderRadius: 2.5, background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <NoteIcon sx={{ color: '#fff', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Pharmacy Billing</Typography>
              <Typography sx={{ color: '#fff', fontSize: '1.35rem', fontWeight: 800, lineHeight: 1.1 }}>Debit Notes</Typography>
            </Box>
            <Box sx={{ ml: 1, px: 1.4, py: 0.4, borderRadius: 10, background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)' }}>
              <Typography sx={{ color: '#fff', fontSize: '0.72rem', fontWeight: 700 }}>{total} total</Typography>
            </Box>
          </Box>

          <Button onClick={() => navigate('/pharmacy/billing/credit-notes')}
            sx={{ zIndex: 1, background: 'rgba(255,255,255,0.15)', color: '#fff !important', fontWeight: 700, fontSize: '0.78rem', borderRadius: 2.5, px: 2.5, py: 1, border: '1.5px solid rgba(255,255,255,0.3)', backdropFilter: 'blur(6px)', '&:hover': { background: 'rgba(255,255,255,0.28)' } }}>
            View Credit Notes
          </Button>
        </Box>

        {/* ── Stat Cards ── */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <StatCard icon={<AccountBalanceWalletIcon />} label="Applied (this page)" value={fmt(appliedTotal)} accent="#2e7d32" sub={`${appliedCount} notes applied`} />
          <StatCard icon={<NoteIcon />} label="Pending Draft" value={draftCount} accent="#f57f17" sub="awaiting application" />
          <StatCard icon={<CheckCircleIcon />} label="Total (this page)" value={rows.length} accent={PURPLE} sub={`of ${total} total`} />
        </Box>

        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, borderRadius: 2.5, fontSize: '0.82rem' }}>{error}</Alert>}
        {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2, borderRadius: 2.5, fontSize: '0.82rem' }}>{success}</Alert>}

        {/* ── Main Card ── */}
        <Card sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', border: '1px solid #f0f2f5', overflow: 'hidden' }}>

          {/* Filter bar */}
          <Box sx={{ px: 2.5, py: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid #f0f4f8', background: '#fafcff' }}>
            <TextField {...kb.searchProps} size="small" placeholder="Search DN number, credit note, patient…  ( / to focus )" value={search}
              onChange={e => handleSearchChange(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#b0bec5', fontSize: 18 }} /></InputAdornment> }}
              sx={{ flex: 1, minWidth: 260, '& .MuiOutlinedInput-root': { borderRadius: 3, fontSize: '0.82rem', background: '#fff', '& fieldset': { borderColor: '#e2e8f0' }, '&:hover fieldset': { borderColor: PURPLE_MID }, '&.Mui-focused fieldset': { borderColor: PURPLE_MID } } }}
            />
            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#b0bec5', fontWeight: 600, mr: 0.5 }}>Filter:</Typography>
              {Object.entries(STATUS).map(([key, s]) => (
                <Chip key={key} label={s.label} size="small" clickable onClick={() => handleStatusFilter(key)}
                  sx={{ fontSize: '0.7rem', fontWeight: 700, height: 26, borderRadius: '20px', transition: 'all 0.18s',
                    background: statusFilter === key ? s.bg : '#f0f4f8', color: statusFilter === key ? s.color : '#90a4ae',
                    border: `1.5px solid ${statusFilter === key ? s.border : '#e2e8f0'}`,
                    '&:hover': { background: s.bg, color: s.color, borderColor: s.border } }}
                />
              ))}
              {statusFilter && (
                <Chip label="Clear" size="small" clickable onClick={() => handleStatusFilter(statusFilter)}
                  sx={{ fontSize: '0.65rem', height: 22, color: '#ef5350', background: '#fff3f3', border: '1px solid #ffcdd2', borderRadius: '20px' }} />
              )}
            </Box>
          </Box>

          {/* Table */}
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
              <CircularProgress sx={{ color: PURPLE_MID }} size={36} />
              <Typography sx={{ fontSize: '0.8rem', color: '#b0bec5' }}>Loading debit notes…</Typography>
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 900 }}>
                <TableBody>
                  <TableRow sx={{ background: `linear-gradient(90deg, ${PURPLE} 0%, ${PURPLE_MID} 100%)` }}>
                    {COL_HEADERS.map(h => (
                      <TableCell key={h} sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'rgba(255,255,255,0.9)', textTransform: 'uppercase', letterSpacing: 0.7, py: 1.4, whiteSpace: 'nowrap', borderBottom: 'none', '&:first-of-type': { pl: 2.5 }, '&:last-of-type': { pr: 2.5 } }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                          <NoteIcon sx={{ fontSize: 52, color: '#e0e0e0', mb: 1.5 }} />
                          <Typography sx={{ fontSize: '0.92rem', fontWeight: 600, color: '#bdbdbd' }}>No debit notes found</Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: '#d0d0d0', mt: 0.5 }}>Try adjusting your search or filter</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : rows.map((dn, i) => {
                    const cn = dn.credit_note || {};
                    const origBill = cn.original_bill || {};
                    return (
                      <TableRow key={dn.id} {...kb.getRowProps(i)} sx={{
                        background: i % 2 === 0 ? '#fff' : '#fdf8ff',
                        borderLeft: dn.status === 'DRAFT' ? `3px solid ${STATUS.DRAFT.dot}` : dn.status === 'APPLIED' ? `3px solid ${STATUS.APPLIED.dot}` : '3px solid transparent',
                        transition: 'background 0.15s',
                        cursor: 'pointer',
                        '&:hover': { background: PURPLE_LIGHT, boxShadow: `inset 0 0 0 1px ${PURPLE_MID}22` },
                        ...(kb.activeIdx === i ? ACTIVE_ROW_SX : {}),
                      }}>

                        {/* DN No */}
                        <TableCell sx={{ py: 1.5, pl: 2.5 }}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, px: 1.2, py: 0.4, borderRadius: 1.5, background: PURPLE_LIGHT, border: `1px solid #ce93d8` }}>
                            <Typography sx={{ fontSize: '0.74rem', fontWeight: 800, color: PURPLE, fontFamily: 'monospace', letterSpacing: 0.3 }}>
                              {dn.debit_note_no}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Date */}
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography sx={{ fontSize: '0.76rem', color: '#546e7a', whiteSpace: 'nowrap' }}>{fmtDate(dn.debit_note_date)}</Typography>
                        </TableCell>

                        {/* CN No */}
                        <TableCell sx={{ py: 1.5 }}>
                          {cn.credit_note_no ? (
                            <Typography sx={{ fontSize: '0.73rem', fontFamily: 'monospace', fontWeight: 700, color: '#6a1b9a' }}>{cn.credit_note_no}</Typography>
                          ) : <Typography sx={{ fontSize: '0.75rem', color: '#ccc' }}>—</Typography>}
                        </TableCell>

                        {/* Patient */}
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#2d3748' }}>{origBill.patient_name || '—'}</Typography>
                          {origBill.bill_no && (
                            <Button size="small" endIcon={<OpenInNewIcon sx={{ fontSize: '11px !important' }} />}
                              onClick={() => navigate(`/pharmacy/billing/${origBill.id}`)}
                              sx={{ fontSize: '0.68rem', color: '#00798E', p: 0, minWidth: 0, fontFamily: 'monospace', fontWeight: 700, '&:hover': { background: 'transparent', textDecoration: 'underline' } }}>
                              {origBill.bill_no}
                            </Button>
                          )}
                        </TableCell>

                        {/* Adj Type */}
                        <TableCell sx={{ py: 1.5 }}>
                          <AdjBadge type={dn.adjustment_type} />
                          {dn.payment_reference && (
                            <Typography sx={{ fontSize: '0.65rem', fontFamily: 'monospace', color: '#aaa', mt: 0.3 }}>
                              {dn.payment_reference}
                            </Typography>
                          )}
                        </TableCell>

                        {/* Amount */}
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography sx={{ fontSize: '0.88rem', fontFamily: 'monospace', fontWeight: 800, color: '#2d3748', whiteSpace: 'nowrap' }}>
                            {fmt(dn.amount)}
                          </Typography>
                        </TableCell>

                        {/* Status */}
                        <TableCell sx={{ py: 1.5 }}>
                          <StatusBadge status={dn.status} />
                          {dn.status === 'APPLIED' && dn.applied_at && (
                            <Typography sx={{ fontSize: '0.62rem', color: '#aaa', mt: 0.3, display: 'block' }}>{fmtDate(dn.applied_at)}</Typography>
                          )}
                          {dn.status === 'CANCELLED' && dn.cancel_reason && (
                            <Typography sx={{ fontSize: '0.62rem', color: '#aaa', mt: 0.3, display: 'block' }}>{dn.cancel_reason}</Typography>
                          )}
                        </TableCell>

                        {/* Actions */}
                        <TableCell sx={{ py: 1.5, pr: 2.5, whiteSpace: 'nowrap' }}>
                          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>

                            {/* Print */}
                            <Tooltip title="Print Debit Note" arrow>
                              <IconButton size="small" onClick={() => {
                                // List row's nested bill refs are attribute-restricted (no
                                // patient/couple/spouse code) — fetch the full enriched
                                // record so the print carries the barcode strip.
                                api.pharmacy.getDebitNote(dn.id)
                                  .then(full => printDebitNote(full || dn, selectedPharmacy))
                                  .catch(() => printDebitNote(dn, selectedPharmacy));
                              }}
                                sx={{ color: PURPLE_MID, border: `1px solid ${PURPLE_MID}33`, borderRadius: 1.5, width: 30, height: 30, '&:hover': { background: PURPLE_LIGHT, borderColor: PURPLE_MID } }}>
                                <PrintIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>

                            {/* Timeline */}
                            {cn.original_bill?.id && (
                              <Tooltip title="View Bill Journey" arrow>
                                <IconButton size="small" onClick={() => openTimeline(dn)}
                                  sx={{ color: '#3949ab', border: '1px solid #3949ab33', borderRadius: 1.5, width: 30, height: 30, '&:hover': { background: '#e8eaf6', borderColor: '#3949ab' } }}>
                                  <TimelineIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>
                            )}

                            {/* Apply (DRAFT only) */}
                            {dn.status === 'DRAFT' && (
                              <Tooltip title="Apply Debit Note" arrow>
                                <Button size="small" variant="contained" onClick={() => setApplyTarget(dn)}
                                  sx={{ fontSize: '0.68rem', fontWeight: 700, borderRadius: 2, py: 0.4, px: 1.2, minWidth: 0, height: 28, background: `linear-gradient(135deg, ${PURPLE}, ${PURPLE_MID})`, color: '#fff !important', boxShadow: `0 2px 8px ${PURPLE}44`, '&:hover': { background: PURPLE, color: '#fff !important' } }}>
                                  Apply
                                </Button>
                              </Tooltip>
                            )}

                            {/* Cancel (DRAFT only) */}
                            {dn.status === 'DRAFT' && (
                              <Tooltip title="Cancel Debit Note" arrow>
                                <IconButton size="small" onClick={() => setCancelTarget(dn)}
                                  sx={{ color: '#d32f2f', border: '1px solid #ffcdd2', borderRadius: 1.5, width: 30, height: 30, '&:hover': { background: '#ffebee', borderColor: '#d32f2f' } }}>
                                  <CancelIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>
                            )}

                            {/* Re-issue (CANCELLED only, if CN might still be open) */}
                            {dn.status === 'CANCELLED' && (
                              <Tooltip title="Re-issue against same credit note" arrow>
                                <Button size="small" variant="outlined" onClick={() => handleReissue(dn)} disabled={reissueLoading}
                                  sx={{ fontSize: '0.68rem', fontWeight: 700, borderRadius: 2, py: 0.4, px: 1.2, minWidth: 0, height: 28, color: '#2e7d32', borderColor: '#a5d6a7', '&:hover': { background: '#e8f5e9', borderColor: '#2e7d32', color: '#2e7d32' } }}>
                                  <ReplayIcon sx={{ fontSize: 13, mr: 0.4 }} />Re-issue
                                </Button>
                              </Tooltip>
                            )}

                            {/* Applied indicator */}
                            {dn.status === 'APPLIED' && (
                              <Tooltip title="Applied" arrow>
                                <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 1.75, borderTop: '1px solid #f0f4f8', background: '#fafcff' }}>
              <Typography sx={{ fontSize: '0.75rem', color: '#90a4ae', fontWeight: 500 }}>
                Showing <b style={{ color: '#546e7a' }}>{(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, total)}</b> of <b style={{ color: '#546e7a' }}>{total}</b>
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton size="small" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                  sx={{ border: '1.5px solid #e2e8f0', borderRadius: 2, width: 32, height: 32, color: page <= 1 ? '#d0d0d0' : PURPLE_MID, '&:hover:not(:disabled)': { background: PURPLE_LIGHT, borderColor: PURPLE_MID } }}>
                  <KeyboardArrowLeftIcon fontSize="small" />
                </IconButton>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, idx) => {
                  const p = totalPages <= 5 ? idx + 1 : (page <= 3 ? idx + 1 : page - 2 + idx);
                  if (p < 1 || p > totalPages) return null;
                  return (
                    <Button key={p} size="small" onClick={() => setPage(p)}
                      sx={{ minWidth: 32, height: 32, borderRadius: 2, fontSize: '0.78rem', fontWeight: 700,
                        border: `1.5px solid ${p === page ? PURPLE_MID : '#e2e8f0'}`,
                        background: p === page ? `linear-gradient(135deg, ${PURPLE}, ${PURPLE_MID})` : 'transparent',
                        color: p === page ? '#fff !important' : '#78909c',
                        boxShadow: p === page ? `0 2px 8px ${PURPLE}44` : 'none',
                        '&:hover': p === page ? { color: '#fff !important' } : { background: PURPLE_LIGHT, borderColor: PURPLE_MID, color: PURPLE_MID } }}>
                      {p}
                    </Button>
                  );
                })}
                <IconButton size="small" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                  sx={{ border: '1.5px solid #e2e8f0', borderRadius: 2, width: 32, height: 32, color: page >= totalPages ? '#d0d0d0' : PURPLE_MID, '&:hover:not(:disabled)': { background: PURPLE_LIGHT, borderColor: PURPLE_MID } }}>
                  <KeyboardArrowRightIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          )}
        </Card>
      </MDBox>

      {/* ── Dialogs ── */}
      <CancelDialog open={!!cancelTarget} dn={cancelTarget} onClose={() => setCancelTarget(null)} onConfirm={handleCancel} />
      <ApplyDialog open={!!applyTarget} dn={applyTarget} onClose={() => setApplyTarget(null)} onConfirm={handleApply} />

      {/* Re-issue via DebitNoteDialog */}
      {reissueCn && reissueTarget && (
        <DebitNoteDialog
          open={!!reissueTarget}
          creditNote={reissueCn}
          contactId={reissueCn.irm_contacts_id}
          onClose={() => { setReissueTarget(null); setReissueCn(null); }}
          onSuccess={() => {
            setReissueTarget(null); setReissueCn(null);
            setSuccess('Debit note re-issued successfully.');
            load(search, statusFilter, page);
          }}
        />
      )}

      <TimelineDialog
        open={showTimeline}
        onClose={() => setShowTimeline(false)}
        title={timelineMeta.title}
        subtitle={timelineMeta.subtitle}
        events={timelineEvents}
        loading={timelineLoading}
      />

      <Footer />
    </DashboardLayout>
  );
}
